import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { PromptInputs, SelectedGamePack } from './CreatePromptDialog';
import { PromptDialogForm } from './PromptDialogForm';
import { listAllGamePacks } from '@/utils/gamePacks';
import { promptGet } from '@/graphql/queries';
import { generateClient, GraphQLQuery } from 'aws-amplify/api';
import { PromptGetQuery } from '@/API';
import { updatePrompt, addPromptToGamePack, removePromptFromGamePack } from '@/client/gamePacks';

const client = generateClient();

interface EditPromptDialogProps {
  promptId?: string;
  isOpen: boolean;

  setIsOpen: (isOpen: boolean) => void;
  onSubmit?: () => void;
}

export const EditPromptDialog = ({
  setIsOpen,
  onSubmit,
  isOpen,
  promptId
}: EditPromptDialogProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<PromptInputs>({
    mode: 'onSubmit',
    defaultValues: {
      gamePacks: []
    }
  });
  const { fields, replace: replaceGamePacks } = useFieldArray({
    control,
    name: 'gamePacks'
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !promptId) return;
      try {
        setIsLoading(true);
        reset();
        const [promptResult, allGamePacks] = await Promise.all([
          client.graphql<GraphQLQuery<PromptGetQuery>>({
            query: promptGet,
            variables: { id: promptId }
          }),
          listAllGamePacks()
        ]);

        // Get the prompt's existing game pack associations
        const prompt = promptResult.data.promptGet;
        if (!prompt) {
          throw new Error('Prompt not found');
        }

        // Get all game packs and check which ones are associated with this prompt
        const allGamePackItems = allGamePacks.data?.gamePackList ?? [];

        // For now, we'll check if the prompt is in each game pack's prompts array
        const gamePacks = allGamePackItems
          .map(
            (gamePack) =>
              gamePack && {
                id: gamePack.id,
                name: gamePack.name,
                selected: gamePack.prompts?.some((p) => p.id === promptId) ?? false
              }
          )
          .filter(Boolean) as SelectedGamePack[];

        setValue('prompt', prompt.text ?? '');
        replaceGamePacks(gamePacks);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [isOpen, promptId, reset, setValue, replaceGamePacks]);

  const onEdit = async (data: PromptInputs) => {
    if (!data.prompt || !promptId) return;
    try {
      setIsSubmitting(true);

      // Update the prompt text
      await updatePrompt(promptId, data.prompt);

      // Handle game pack associations
      const selectedGamePacks = data.gamePacks.filter((gamePack) => gamePack.selected);
      const unselectedGamePacks = data.gamePacks.filter((gamePack) => !gamePack.selected);

      // Get current associations to determine what to add/remove
      const allGamePacks = await listAllGamePacks();
      const currentAssociations =
        allGamePacks.data?.gamePackList?.filter((gp) =>
          gp.prompts?.some((p) => p.id === promptId)
        ) || [];

      // Remove associations for unselected game packs
      const toRemove = currentAssociations.filter((gp) =>
        unselectedGamePacks.some((ugp) => ugp.id === gp.id)
      );

      // Add associations for newly selected game packs
      const toAdd = selectedGamePacks.filter(
        (gp) => !currentAssociations.some((ca) => ca.id === gp.id)
      );

      // Execute the operations
      const removePromises = toRemove.map((gp) => removePromptFromGamePack(gp.id, promptId));
      const addPromises = toAdd
        .filter((gp) => gp.id) // Ensure id exists
        .map((gp) => addPromptToGamePack(gp.id!, promptId));

      await Promise.all([...removePromises, ...addPromises]);

      setIsSubmitting(false);
      setIsOpen(false);
      if (onSubmit) {
        onSubmit();
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
      <DialogTitle className="text-[24px] font-bold">Edit prompt</DialogTitle>
      <PromptDialogForm
        register={register}
        fields={fields}
        errors={errors}
        onCancel={() => {
          reset();
          setIsOpen(false);
        }}
        onSubmit={handleSubmit(onEdit)}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  );
};
